import { useEffect, useState } from 'react';
import { Box, Card, Grid, Stack, Typography } from '@mui/material';
import { Form, FormikProvider, useFormik } from 'formik';
import AddLogoDialog from 'pages/logos/components/AddLogoDialog';
import ReactTimeAgo from 'react-time-ago';
import * as Yup from 'yup';
import { ChildTenant } from '../../../@types/organization';
import {
  updateCurrentTenant,
  updateOrgLogo,
  getCurrentTenant
} from '../../../redux/slices/organization';
import { RootState, useSelector } from '../../../redux/store';
import MyAvatar from 'components/util/MyAvatar';
import useCustomDispatch from 'redux/dispatch';
import { Button, TextField } from '@nps.today/component-library';
import { getPhoneNumberYupTest } from 'utils/validation/isPhoneNumberValid';
import { getVatYupTest } from 'utils/validation/isVatValid';
import PhoneNumberInputField from 'components/util/PhoneNumberInputField';
import { useRegisterUnsavedFormChanges } from 'components/form/useRegisterUnsavedFormChanges';
import getFormikValidateFunction from 'utils/getFormikValidateFunction';
import { useTranslator } from 'translation/useTranslator';
import AccessGuard, { rules, useAccess } from 'access';
import useDisplayMessage from 'components/messages/useMessage';

const componentDataTestId = 'organization-general';

export default function OrganizationGeneral() {
  const translator = useTranslator();
  const customDispatch = useCustomDispatch();
  const [displayAddLogoDialog, setDisplayAddLogoDialog] = useState(false);
  const [tenant, setTenant] = useState<ChildTenant | null>(null);
  const { isLogoLoading } = useSelector((state: RootState) => state.organization);
  const displayMessage = useDisplayMessage();
  const canEdit = useAccess(rules.tenant.update);

  useEffect(() => {
    customDispatch({
      action: getCurrentTenant,
      disableAllMessages: true,
      onSuccess: (response) => setTenant(response.result?.data)
    });
  }, [customDispatch]);

  const UpdateOrgSchema = Yup.object().shape({
    displayName: Yup.string().min(2).required(translator.nameRequired()),
    phoneNumber: Yup.string().nullable().test(getPhoneNumberYupTest()),
    vat: Yup.string().required(translator.vatIsRequired()).test(getVatYupTest()),
    website: Yup.string().matches(
      /^(((https?):\/\/)?(www\.)?[a-z0-9]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?)?\)?$/,
      'Invalid URL'
    )
  });

  const formik = useFormik<ChildTenant>({
    enableReinitialize: true,
    initialValues: {
      id: tenant?.id || '',
      displayName: tenant?.displayName || '',
      name: tenant?.name || '',
      phoneNumber: tenant?.phoneNumber || '',
      deactivationReminderEmail: tenant?.deactivationReminderEmail || '',
      invoiceEmail: tenant?.invoiceEmail || '',
      vat: tenant?.vat || '',
      deactivateAt: tenant?.deactivateAt,
      active: tenant?.active ?? false,
      isMfaForced: tenant?.isMfaForced ?? false,
      isPasswordLoginDisabled: tenant?.isPasswordLoginDisabled ?? false,
      anonymized: tenant?.anonymized ?? false,
      logoLink: tenant?.logoLink || '',
      website: tenant?.website || '',
      smsEnabled: tenant?.smsEnabled || false
    },
    validate: getFormikValidateFunction(UpdateOrgSchema, {
      initialorganizationPhone: tenant?.phoneNumber,
      initialvat: tenant?.vat || ''
    }),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      customDispatch({
        action: updateCurrentTenant,
        actionParameters: values,
        onFinally: () => {
          setSubmitting(false);
        },
        onSuccess: (response) => {
          setTenant(response.result?.data);
        }
      });
    }
  });

  const { values, errors, touched, isSubmitting, handleSubmit, getFieldProps, dirty } = formik;

  useRegisterUnsavedFormChanges(dirty);

  const createdString = getFieldProps('created')?.value;
  const createdDate = new Date(createdString);

  return (
    <>
      <Card sx={{ py: 3, mt: 3, width: '100%' }}>
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Grid container spacing={3} sx={{ p: 3 }}>
              <Grid size={12}>
                <Stack spacing={{ xs: 2, md: 3 }}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <TextField
                      dataTestId={`${componentDataTestId}-display-name`}
                      fullWidth
                      label={translator.displayName()}
                      {...getFieldProps('displayName')}
                      error={Boolean(touched.displayName && errors.displayName)}
                      helperText={touched.displayName && errors.displayName}
                      description={translator.displayNameDescription()}
                      disabled={!canEdit}
                    />
                    <TextField
                      dataTestId={`${componentDataTestId}-organization-name`}
                      fullWidth
                      label={translator.organizationName()}
                      {...getFieldProps('name')}
                      error={Boolean(touched.name && errors.name)}
                      helperText={touched.name && errors.name}
                      description={translator.organizationNameHelperText(
                        values.name.length ? values.name : 'X'
                      )}
                      disabled={!canEdit}
                    />
                  </Stack>

                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <PhoneNumberInputField
                      dataTestId={`${componentDataTestId}-organization`}
                      fieldName="phoneNumber"
                      label={translator.organizationPhone()}
                      formik={formik}
                      initialValue={tenant?.phoneNumber || ''}
                      disabled={!canEdit}
                    />
                    <TextField
                      dataTestId={`${componentDataTestId}-invoice-email`}
                      fullWidth
                      label={translator.invoceEmail()}
                      {...getFieldProps('invoiceEmail')}
                      error={Boolean(touched.invoiceEmail && errors.invoiceEmail)}
                      helperText={touched.invoiceEmail && errors.invoiceEmail}
                      disabled={!canEdit}
                    />
                  </Stack>

                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <TextField
                      dataTestId={`${componentDataTestId}-vat`}
                      fullWidth
                      label={translator.vat()}
                      {...getFieldProps('vat')}
                      error={Boolean(touched.vat && errors.vat)}
                      helperText={touched.vat && errors.vat}
                      disabled={!canEdit}
                    />
                    <TextField
                      dataTestId={`${componentDataTestId}-website`}
                      copyOptions={{
                        enable: true,
                        tooltipText: translator.copyToClipboard(),
                        onSuccess: () =>
                          displayMessage.success({
                            message: {
                              title: translator.copiedXToClipboard(translator.website())
                            }
                          })
                      }}
                      fullWidth
                      label={translator.website()}
                      {...getFieldProps('website')}
                      error={Boolean(touched.website && errors.website)}
                      helperText={touched.website && errors.website}
                      disabled={!canEdit}
                    />
                  </Stack>

                  <TextField
                    dataTestId={`${componentDataTestId}-account-id`}
                    copyOptions={{
                      enable: true,
                      tooltipText: translator.copyToClipboard(),
                      onSuccess: () =>
                        displayMessage.success({
                          message: {
                            title: translator.copiedXToClipboard(translator.accountId())
                          }
                        })
                    }}
                    disabled
                    fullWidth
                    label={translator.accountId()}
                    {...getFieldProps('id')}
                    error={Boolean(touched.id && errors.id)}
                    helperText={touched.id && errors.id}
                  />
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <Stack
                      direction={{ xs: 'column' }}
                      sx={{ width: '100%' }}
                      justifyContent="center"
                      alignItems="center"
                    >
                      {createdString && (
                        <>
                          <Stack
                            direction={{ xs: 'row' }}
                            sx={{ width: '100%' }}
                            alignItems="center"
                            justifyContent="center"
                          >
                            <Typography>{translator.accountCreated()}&nbsp;</Typography>
                            <ReactTimeAgo date={createdDate} locale="en-US" timeStyle="round" />
                          </Stack>
                          <Typography>{createdDate?.toLocaleDateString()}</Typography>
                        </>
                      )}
                    </Stack>
                  </Stack>
                </Stack>

                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                  <AccessGuard rule={rules.tenant.update}>
                    <Button
                      dataTestId={`${componentDataTestId}-save`}
                      type="submit"
                      variant="contained"
                      loading={isSubmitting}
                      disabled={!dirty}
                    >
                      {translator.saveChanges()}
                    </Button>
                  </AccessGuard>
                </Box>
              </Grid>
            </Grid>
          </Form>
        </FormikProvider>
      </Card>
      <Card
        sx={{
          py: 3,
          mt: 3,
          width: '100%',
          display: 'flex',
          gap: '1rem',
          flexDirection: 'column',
          alignItems: 'center',
          overflow: 'inherit'
        }}
      >
        <MyAvatar
          style={{
            width: '10rem',
            height: '10rem'
          }}
        />

        <AccessGuard rule={rules.tenant.update}>
          <Button
            dataTestId={`${componentDataTestId}-change-account-logo`}
            loading={isLogoLoading}
            variant="contained"
            onClick={() => setDisplayAddLogoDialog(true)}
            sx={{ width: 'max-content' }}
          >
            {translator.changeAccountLogo()}
          </Button>
        </AccessGuard>
      </Card>
      <AddLogoDialog
        display={displayAddLogoDialog}
        hideModal={() => setDisplayAddLogoDialog(false)}
        uploadLogo={updateOrgLogo}
      />
    </>
  );
}
